var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import { isNil, useClasses, useProps } from '@hawkins/helpers';
import { FormFieldContainer } from '../formFieldContainer';
import { FormGroup, FormGroupContext } from '../formGroup';
import { CheckboxGroupContext } from './checkboxGroup.context';
var displayName = 'CheckboxGroup';
export var CheckboxGroup = forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _b = useProps(displayName, props), classesProp = _b.classes, className = _b.className, containerClasses = _b.containerClasses, containerProps = _b.containerProps, _c = _b.defaultValue, defaultValue = _c === void 0 ? [] : _c, description = _b.description, _d = _b.disabled, disabledProp = _d === void 0 ? false : _d, label = _b.label, onChangeProp = _b.onChange, _e = _b.orientation, orientation = _e === void 0 ? 'vertical' : _e, requirementType = _b.requirementType, validationMessage = _b.validationMessage, validationStatus = _b.validationStatus, valueProp = _b.value, restProps = __rest(_b, ["classes", "className", "containerClasses", "containerProps", "defaultValue", "description", "disabled", "label", "onChange", "orientation", "requirementType", "validationMessage", "validationStatus", "value"]);
    var isValueControlled = !isNil(valueProp);
    // State
    var _f = __read(useState(defaultValue), 2), internalValue = _f[0], setInternalValue = _f[1];
    var value = valueProp || internalValue;
    // Read from the form group context, if it is inside a <FormGroup/>
    var formGroupContext = useContext(FormGroupContext);
    var disabled = (formGroupContext === null || formGroupContext === void 0 ? void 0 : formGroupContext.disabled) || disabledProp;
    // Callbacks
    var onChange = useCallback(function (ev) {
        var newValue = value.filter(function (v) { return v !== ev.target.value; });
        if (ev.target.checked)
            newValue.push(ev.target.value);
        if (!isValueControlled)
            setInternalValue(newValue);
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(newValue);
    }, [isValueControlled, onChangeProp, value]);
    // Context
    var groupContext = useMemo(function () { return ({ disabled: disabled, onChange: onChange, value: value }); }, [disabled, onChange, value]);
    // Styles
    // We use `useClasses` and `rootClassName` slightly differently than in most of our other components
    // since this component is largely a pass-through for `FormGroup`.
    var classes = useClasses(displayName, {}, classesProp);
    var rootClassName = clsx(classes.HAWKINS_BASE, className);
    return (
    // TODO(a11y): Is the `label` in `FormFieldContainer` the correct way to label an input group?
    // Might need to update `FormFieldContainer` and/or `FormGroup` to properly handle labeling groups.
    //
    // TODO(API): Rewrite this component and maybe `FormFieldContainer` and/or `FormGroup`
    // so that `ref` and `restProps` are applied to the root element.
    // It feels unintuitive that this component doesn't work the same way as most of our other components.
    _jsx(FormFieldContainer, __assign({ description: description, label: label, requirementType: requirementType, validationMessage: validationMessage, validationStatus: validationStatus, classes: containerClasses }, containerProps, { children: _jsx(CheckboxGroupContext.Provider, __assign({ value: groupContext }, { children: _jsx(FormGroup, __assign({ classes: classesProp, className: rootClassName, disabled: disabled, gap: orientation === 'horizontal' ? 'space2' : 'space0', orientation: orientation, ref: ref, role: "group" }, restProps, { children: children })) })) })));
});
CheckboxGroup.displayName = displayName;
